import { useEffect, useState } from 'react';

import {
    ChoiceCards,
    ErrorMessage,
    IChoiceCardsValues,
    Notification,
} from '@honeycomb-npm/honeycomb-react';

import { DynamicContent } from '../../../utils/DynamicContent';

import BladeHeading from '../../../sharedComponents/BladeHeading';

import { ApiStatus } from '../../../types';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import {
    selectGetLoanPurposeApiStatus,
    selectLoanPurposeAEM,
    selectLoanPurposeIds,
    selectLoanPurposes,
    selectUpdateLoanPurposeRequest,
} from '../loanPurposeSelectors';
import {
    getLoanPurposes,
    resetLoanPurposesStatus,
    updateLoanPurpose,
    updateSelectedLoanPurpose,
} from '../loanPurposeSlice';
import { SpinnerWrapper } from '../../../sharedComponents';
import { defaultLoanPurposePictogramMap, LoanPurposeBladeProps } from '../loanPurposeTypes';

import { useIsFocusedHeading } from '../../../utils/useIsFocusedHeading';

export const LoanPurpose = ({
    isMultipleSelect = false,
    isHiddenSelectField = true,
    columnsGrid = 3,
    isFocusedBlade,
    disableReset = false,
}: LoanPurposeBladeProps) => {

    const dispatch = useAppDispatch();

    const {
        heading,
        infoHeader,
        infoDescription,
        selectionLabel,
        selectText,
        description,
        confirmButtonText,
        validations,
    } = useAppSelector(selectLoanPurposeAEM);
    const getLoanPurposeApiStatus = useAppSelector(selectGetLoanPurposeApiStatus);
    const loanPurposes = useAppSelector(selectLoanPurposes);
    const selectedLoanPurposeIds = useAppSelector(selectLoanPurposeIds);
    const updateLoanPurposeRequest = useAppSelector(selectUpdateLoanPurposeRequest);

    const choiceCardsType = isMultipleSelect ? 'checkbox' : 'radio';

    const [showError, setShowError] = useState(false);
    const [selectedOptions, setSelectedOptions] = useState<IChoiceCardsValues>({});

    const isFocusedHeading = useIsFocusedHeading({
        isReady: getLoanPurposeApiStatus === ApiStatus.Fulfilled,
        isFocused: isFocusedBlade,
    });

    const changeHandler = (name: string, value: IChoiceCardsValues) => {
        setSelectedOptions(value);
        const options: number[] = [];

        for (const key in value) {
            if (value[key]) {
                options.push(Number(key));
            }
        }
        dispatch(updateSelectedLoanPurpose(options));
        setShowError(false);
    };

    const getDefaultLoanPurposePictogramById = (id: number): string => {

        const index = defaultLoanPurposePictogramMap.findIndex(({ loanPurposeId }) => loanPurposeId === id);

        return index !== -1 ? defaultLoanPurposePictogramMap[index].pictogram : '';

    };

    const handleLoanPurposeSubmit = () => {
        if (selectedLoanPurposeIds.length > 0) {
            const request = {
                'applicationNumber': updateLoanPurposeRequest.applicationNumber,
                'selectedLoanPurposeIds': selectedLoanPurposeIds,
            };

            dispatch(updateLoanPurpose(request));
        }
        else {
            setShowError(true);
            document.getElementById('loan-purpose-selection-errormessage')?.focus();
        }
    };

    useEffect(() => {
        if (showError) {
            document.getElementById('loan-purpose-selection-errormessage')?.focus();
            setShowError(true);
        }
    }, [showError]);

    useEffect(() => {

        if (getLoanPurposeApiStatus === ApiStatus.Rejected || getLoanPurposeApiStatus === ApiStatus.Idle) {
            dispatch(getLoanPurposes());
        }
        
        return () => {
            if (!disableReset) {
                dispatch(resetLoanPurposesStatus());
            }
        };
    }, []);

    return (
        <>
            {getLoanPurposeApiStatus === ApiStatus.Rejected
                ? (
                    <ErrorMessage
                        forceVisible
                        iconType="svgfile"
                        id="loan-purpose-error"
                        message="Failed to get loan purpose options"
                    />)
                : (
                    <SpinnerWrapper id='loan-purpose' isLoaded={getLoanPurposeApiStatus === ApiStatus.Fulfilled}>
                        <BladeHeading id='loan-purpose-main-heading' isFocused={isFocusedHeading}>
                            <DynamicContent content={heading} id='loan-purpose-main-heading-content'></DynamicContent>
                        </BladeHeading>
                        <DynamicContent
                            content={description}
                            id="loan-purpose-main-description"
                            isParagraph
                        />
                        {infoDescription &&
                            <>
                                <Notification
                                    type='info'
                                    heading={infoHeader}
                                    animation={false}
                                    isOpen
                                    isAutoFocus={false}
                                >
                                    <DynamicContent
                                        id="loan-purpose-main-notification"
                                        content={infoDescription}
                                    />
                                </Notification>
                                <hr aria-hidden='true' className='mt-0 mb-2' />
                            </>}
                        {isHiddenSelectField
                            ?
                            <ChoiceCards
                                buttons={[
                                    {
                                        children: confirmButtonText,
                                        id: 'loan-purpose-confirm-button',
                                        onClick: handleLoanPurposeSubmit,
                                    },
                                ]}
                                displaySelectField
                                errormessage={validations.errorMessage}
                                id='loan-purpose-selections'
                                label={selectionLabel}
                                name='loan-purpose-selection'
                                cardsGridClasses={`col-md-6 col-lg-${columnsGrid}`}
                                isValid={!showError}
                                smallerOnMobile
                                onChange={changeHandler}
                                options={loanPurposes.map(({ title, name, pictogram, id, description: itemDes }) => ({
                                    id: `loan-purpose-${title.replace(/\s+/g, '').toLowerCase()}`,
                                    name,
                                    pictogram: pictogram || getDefaultLoanPurposePictogramById(id),
                                    pictogramTheme: 'beacon',
                                    title,
                                    value: `${id}`,
                                    description: itemDes,
                                }))}
                                selectText={selectText}
                                type={choiceCardsType}
                                value={selectedOptions} />
                            :
                            <ChoiceCards
                                buttons={[
                                    {
                                        children: confirmButtonText,
                                        id: 'loan-purpose-confirm-button',
                                        onClick: handleLoanPurposeSubmit,
                                    },
                                ]}
                                errormessage={validations.errorMessage}
                                id='loan-purpose-selections'
                                label={selectionLabel}
                                name='loan-purpose-selection'
                                cardsGridClasses={`col-md-6 col-lg-${columnsGrid}`}
                                isValid={!showError}
                                smallerOnMobile
                                onChange={changeHandler}
                                options={loanPurposes.map(({ title, name,  id, description: itemDes }) => ({
                                    id: `loan-purpose-${title.replace(/\s+/g, '').toLowerCase()}`,
                                    name,
                                    pictogram: getDefaultLoanPurposePictogramById(id),
                                    pictogramTheme: 'beacon',
                                    title,
                                    value: `${id}`,
                                    description: itemDes,
                                }))}
                                selectText={selectText}
                                type={choiceCardsType}
                                value={selectedOptions} />}
                    </SpinnerWrapper>
                )
            }
        </>
    );
};
